import { Injectable } from '@angular/core';
import { Action, Selector, State, Store, type StateContext } from '@ngxs/store';
import * as FlatModels from '@medsurf/flat-models';
import * as FlatActions from '@medsurf/flat-actions';
import * as FlatServices from '@medsurf/flat-services';
import * as FlatStates from '../../internal';

export interface MediaAuthorStateModel {
  mediaSearch: FlatModels.MediaAuthorModels.MediaSearch;
  total: number;
  perPage: number;
  mediaResults: FlatModels.MediaAuthorModels.SearchResult[];
  mediaUsedIn: Record<string, FlatModels.MediaAuthorModels.MediaUsedInResultEntry[]>;
}

@State<MediaAuthorStateModel>({
  name: 'mediaAuthor',
  defaults: {
    mediaSearch: {
      searchString: '',
      typeFilter: [],
      uploadedByMe: false,
      usedInFilter: [],
      page: 1,
      order: FlatModels.MediaAuthorModels.MediaOrder.NONE,
      orderBy: '',
    },
    total: 0,
    perPage: 0,
    mediaResults: [],
    mediaUsedIn: {}
  }
})
@Injectable()
export class MediaAuthorState {
  constructor(public store: Store,
              public apiControlService: FlatServices.ApiControlService) {
  }

  //<editor-fold desc="Selectors">

  @Selector([MediaAuthorState])
  static mediaSearch$(state: MediaAuthorStateModel): FlatModels.MediaAuthorModels.MediaSearch {
    return state.mediaSearch;
  }

  @Selector([MediaAuthorState])
  static mediaResults$(state: MediaAuthorStateModel): FlatModels.MediaAuthorModels.SearchResult[] {
    return state.mediaResults;
  }
  
  @Selector([MediaAuthorState])
  static mediaUsedIn$(state: MediaAuthorStateModel) {
    return state.mediaUsedIn;
  }
  
  @Selector([MediaAuthorState.mediaUsedIn$])
  static mediaUsedInById$(mediaUsedIn: Record<string, FlatModels.MediaAuthorModels.MediaUsedInResultEntry[]>) {
    return (id: string) => {
      return mediaUsedIn && mediaUsedIn[id] !== undefined ? mediaUsedIn[id] : null;
    }
  }

  @Selector([
    MediaAuthorState.mediaResults$,
    FlatStates.MediaEntityState.typedEntities$
  ])
  static mediaResultMedias$(results: FlatModels.MediaAuthorModels.SearchResult[],
                            typedEntities: { [id: string]: FlatModels.MediaEntityModels.MediaEntityType }): FlatModels.MediaEntityModels.MediaEntityType[] {
    return results.reduce((arr, result) => {
      const entity = typedEntities[result.id];
      if (entity) {
        arr.push(entity);
      }
      return arr;
    }, [] as FlatModels.MediaEntityModels.MediaEntityType[]);
  }

  //</editor-fold>

  //<editor-fold desc="Actions">

  @Action(FlatActions.MediaAuthorActions.GetMediasByIds)
  getMedia(_: StateContext<MediaAuthorStateModel>,
           {ids, force}: FlatActions.MediaAuthorActions.GetMediasByIds) {
    let filtered;
    if (!force) {
      const typedMedias = this.store.selectSnapshot(FlatStates.MediaEntityState.typedEntities$);
      filtered = ids.reduce((arr, id) => {
        const entity = typedMedias[id];
        if (!entity) {
          arr.push(id)
        }
        return arr;
      }, [] as string[]);
    } else {
      filtered = ids;
    }
    
    if (filtered?.length === 0) {
      return;
    }
    
    return this.apiControlService.sendMessage(
      new FlatActions.MediaAuthorActions.GetMediasByIdsRequest(filtered)
    );
  }

  @Action(FlatActions.MediaAuthorActions.GetMediasUsedInSuccess)
  getMediaUsedInSuccess({getState, patchState}: StateContext<MediaAuthorStateModel>,
                        {entityQueryResults}: FlatActions.MediaAuthorActions.GetMediasUsedInSuccess) {
    // Get Media Used In
    const mediaUsedIn = structuredClone(getState().mediaUsedIn);
    
    // Iterate throuh the results
    for (const entityQueryResult of entityQueryResults) {
      mediaUsedIn[entityQueryResult.entity.id] = entityQueryResult.entity.results;
    }
    
    // Patch State
    patchState({
      mediaUsedIn
    });
  }

  @Action(FlatActions.MediaAuthorActions.GenerateSlidePdf)
  generateSlidePdf(_: StateContext<MediaAuthorStateModel>,
                   {id, forAuthor}: FlatActions.MediaAuthorActions.GenerateSlidePdf) {
    return this.apiControlService.sendMessage(
      new FlatActions.MediaAuthorActions.GenerateSlidePdfRequest(id, forAuthor)
    );
  }

  @Action(FlatActions.MediaAuthorActions.GenerateChapterPdf)
  generateChapterPdf(_: StateContext<MediaAuthorStateModel>,
                     {id, forceGeneration, forAuthor}: FlatActions.MediaAuthorActions.GenerateChapterPdf) {
    return this.apiControlService.sendMessage(
      new FlatActions.MediaAuthorActions.GenerateChapterPdfRequest(id, forceGeneration, forAuthor)
    );
  }
  
  @Action(FlatActions.MediaAuthorActions.AddWatermark)
  addWatermark(_: StateContext<MediaAuthorStateModel>,
               {media, position}: FlatActions.MediaAuthorActions.AddWatermark) {
    if (!media?.mediaCopyrights || !media?.id) {
      return;
    }
    
    const typedCopyrights = this.store.selectSnapshot(FlatStates.CopyrightEntityState.typedEntities$);
    const lines = media.mediaCopyrights
      .sort((a, b) => a.order - b.order)
      .reduce((arr, mediaCopyright) => {
        const copyrightId = mediaCopyright.copyright;
        const entity = typedCopyrights[copyrightId];
        if (entity && entity.text) {
          arr.push(`${entity.text} ${mediaCopyright.text}`);
        }
        return arr;
      }, [] as string[]);
    
    return this.apiControlService.sendMessage(
      new FlatActions.MediaAuthorActions.AddWatermarkRequest(media.id, lines, position)
    );
  }

  //</editor-fold>
}
